import {
  Briefcase,
  Building2,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  CreditCard,
  Database,
  Download,
  Eye,
  File as FileIcon,
  FileText,
  FileUp,
  GraduationCap,
  Image as ImageIcon,
  LogOut,
  Mail,
  MapPin,
  Megaphone,
  Rocket,
  Search,
  Settings,
  Shield,
  Tags,
  UserCog,
  Users,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AdminBreadcrumb } from '@/components/common/AdminBreadcrumb';
import { DebugInfoPanel } from '@/components/DebugInfoPanel';
import { UserSwitcher } from '@/components/dev/UserSwitcher';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { apiUrl } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { type Tenant, useTenant } from '@/contexts/TenantContext';
import { detectAppContext } from '@/lib/context';
import { cn } from '@/lib/utils';

const API_BASE = apiUrl('/api');

interface MenuItem {
  label: string;
  path: string;
  icon: React.ElementType;
  roles?: string[];
}

interface MenuCategory {
  category: string;
  categoryId: string;
  icon: React.ElementType;
  items: MenuItem[];
  requiresTenant?: boolean; // If true, only show when a tenant is selected
  notCollapsible?: boolean; // If true, do not show chevrons or allow toggling
}

// Helper to check if user is system admin
const isSystemAdmin = (role: string) => {
  return ['system_admin', 'system_operator', 'system_support'].includes(role);
};

// Menu configuration with translation keys and icons
// Reorganized based on User Stories and seasonal business flow
const menuConfig: MenuCategory[] = [
  // ===== システム管理 (Platform Only) =====
  // ===== パートナー管理 (Tenant Management) =====
  {
    category: 'menu.partnerManagement',
    categoryId: 'tenants',
    icon: Building2,
    requiresTenant: false,
    items: [
      {
        label: 'menu.tenants',
        path: '/system/tenants',
        icon: Building2,
        roles: ['system_admin', 'system_operator'],
      },
      {
        label: 'menu.institutions',
        path: '/system/institutions',
        icon: GraduationCap,
        roles: ['system_admin', 'system_operator'],
      },
    ],
    notCollapsible: true,
  },

  // ===== システム管理 (System Administration) =====
  {
    category: 'menu.systemAdmin',
    categoryId: 'system_admin',
    icon: Shield,
    requiresTenant: false,
    items: [
      {
        label: 'menu.supportStaff',
        path: '/system/admins',
        icon: UserCog,
        roles: ['system_admin'],
      },
      {
        label: 'vendors.title',
        path: '/system/vendors',
        icon: UserCog, // Or generic icon
        roles: ['system_admin'],
      },
    ],
    notCollapsible: true,
  },

  // ===== サイト構築 (出願前・準備期) - US-4系 =====
  {
    category: 'menu.siteSetup',
    categoryId: 'site',
    icon: Database,
    requiresTenant: true,
    items: [
      {
        label: 'menu.organizations',
        path: '/site/organizations',
        icon: Building2,
      },
      { label: 'menu.exams', path: '/site/exams', icon: Calendar },
      { label: 'menu.venues', path: '/site/venues', icon: MapPin },
      {
        label: 'menu.changeRequests',
        path: '/site/change-requests',
        icon: ClipboardList,
      },
      {
        label: '届け出',
        path: '/site/notifications',
        icon: ClipboardList,
      },
      { label: 'menu.labels', path: '/site/labels', icon: Tags },
    ],
  },

  // ===== 出願期対応 (出願受付中) - US-1系 =====
  {
    category: 'menu.applicationPhase',
    categoryId: 'applications',
    icon: FileText,
    requiresTenant: true,
    items: [
      { label: 'menu.appSearch', path: '/applications/search', icon: Search },
      {
        label: 'menu.accountSearch',
        path: '/applications/accounts',
        icon: Users,
      },
      {
        label: 'menu.appStatus',
        path: '/applications/status',
        icon: CheckCircle,
      },
      { label: 'menu.appUpload', path: '/applications/upload', icon: FileUp },
    ],
  },

  // ===== 選考・結果発表 (試験後・発表期) - US-1拡張 =====
  {
    category: 'menu.selectionPhase',
    categoryId: 'selection',
    icon: GraduationCap,
    requiresTenant: true,
    items: [
      {
        label: 'menu.selectionResults',
        path: '/selection/results',
        icon: CheckCircle,
      },
      {
        label: 'menu.resultAnnouncement',
        path: '/selection/announcement',
        icon: Megaphone,
      },
    ],
  },

  // ===== 入学手続き (合格後・入学準備期) - US-3系 =====
  {
    category: 'menu.enrollmentPhase',
    categoryId: 'enrollment',
    icon: GraduationCap,
    requiresTenant: true,
    items: [
      {
        label: 'menu.enrollStudents',
        path: '/enrollment/students',
        icon: Users,
      },
      { label: 'menu.fees', path: '/enrollment/fees', icon: CreditCard },
      { label: 'menu.enrollUpload', path: '/enrollment/upload', icon: FileUp },
      {
        label: 'menu.payments',
        path: '/enrollment/payments',
        icon: CreditCard,
      },
    ],
  },

  // ===== コミュニケーション - US-2系 =====
  {
    category: 'menu.communication',
    categoryId: 'comms',
    icon: Megaphone,
    requiresTenant: true,
    items: [
      {
        label: 'menu.announcements',
        path: '/comms/announcements',
        icon: Megaphone,
      },
      { label: 'menu.messages', path: '/comms/messages', icon: Mail },
      { label: 'menu.emails', path: '/comms/emails', icon: Mail },
    ],
  },

  // ===== 帳票・出力 - US-5系 =====
  {
    category: 'menu.reports',
    categoryId: 'reports',
    icon: Download,
    requiresTenant: true,
    items: [
      { label: 'menu.batchDl', path: '/reports/batch', icon: Download },
      {
        label: 'menu.appReports',
        path: '/reports/application',
        icon: FileText,
      },
      {
        label: 'menu.enrollReports',
        path: '/reports/enrollment',
        icon: GraduationCap,
      },
      { label: 'menu.fileDl', path: '/reports/files', icon: Download },
    ],
  },

  // ===== 資料・コンテンツ - US-403 =====
  {
    category: 'menu.contentAssets',
    categoryId: 'content',
    icon: FileIcon,
    requiresTenant: true,
    items: [
      { label: 'menu.documents', path: '/content/documents', icon: FileIcon },
      { label: 'menu.static', path: '/content/static', icon: Eye },
    ],
  },

  // ===== 設定・管理 - US-6系 =====
  {
    category: 'menu.settings',
    categoryId: 'settings',
    icon: Settings,
    requiresTenant: true,
    items: [
      { label: 'menu.users', path: '/settings/users', icon: Users },
      {
        label: 'menu.permissions',
        path: '/settings/permissions',
        icon: Shield,
      },
      { label: 'menu.basic', path: '/settings/basic', icon: Settings },
      { label: 'menu.branding', path: '/settings/branding', icon: ImageIcon },
    ],
  },

  // ===== 監査・ログ - US-602 =====
  {
    category: 'menu.audit',
    categoryId: 'audit',
    icon: Shield,
    requiresTenant: false,
    items: [{ label: 'menu.logs', path: '/audit/logs', icon: FileText }],
  },

  // ===== リリース - US-404 =====
  {
    category: 'menu.release',
    categoryId: 'release',
    icon: Rocket,
    requiresTenant: true,
    items: [
      { label: 'menu.preview', path: '/release/preview', icon: Eye },
      { label: 'menu.deploy', path: '/release/deploy', icon: Rocket },
    ],
  },
];

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { selectedTenant, setSelectedTenant } = useTenant();
  // Tenant fetching for dropdown removed.

  useEffect(() => {
    if (user && ['system_admin', 'system_operator', 'system_support'].includes(user.role)) {
      // Logic for system admins if needed
    } else if (user?.tenantId && !selectedTenant) {
      // Auto-select tenant for non-system admins (University Staff)
      const fetchMyTenant = async () => {
        try {
          // Retrieve token from storage manually since it's not exposed in context
          const stored = localStorage.getItem('admission-system-auth');
          const token = stored ? JSON.parse(stored).accessToken : '';

          const response = await fetch(`${API_BASE}/tenants/${user.tenantId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = (await response.json()) as Tenant;
            setSelectedTenant(data);
          } else {
            console.error(`[AdminLayout] Fetch Tenant Failed: ${response.status} ${response.statusText}`);
            const text = await response.text();
            console.error(`[AdminLayout] Error Body: ${text}`);
            console.log(`[AdminLayout] Token used: ${token ? 'YES' : 'NO'} (${token?.substring(0, 10)}...)`);
          }
        } catch (error) {
          console.error('Failed to fetch my tenant:', error);
        }
      };
      fetchMyTenant();
    }
  }, [user, selectedTenant, setSelectedTenant]);

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Click outside for user menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine which categories should be open by default based on current path
  const getInitialOpenCategories = () => {
    const open: Record<string, boolean> = {};
    for (const group of menuConfig) {
      if (['tenants', 'system_admin'].includes(group.categoryId)) {
        open[group.categoryId] = true; // Always open Platform Admin groups
      } else {
        const isActive = group.items.some((item) =>
          location.pathname.startsWith(item.path.split('/').slice(0, 4).join('/')),
        );
        open[group.categoryId] = isActive;
      }
    }
    if (!Object.values(open).some(Boolean)) {
      open.master = true;
    }
    return open;
  };

  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>(getInitialOpenCategories);

  const toggleCategory = (categoryId: string) => {
    // Prevent collapsing for system admin groups
    if (['tenants', 'system_admin'].includes(categoryId)) return;

    setOpenCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const isSystemUser = user && isSystemAdmin(user.role);

  // Debug / Health Check
  interface ServerStatus {
    status: string;
    service: string;
    version: string;
    debug?: {
      env: string;
      region: string;
      databases?: {
        platform: boolean;
        admissions: boolean;
      };
      auth: string;
    };
  }

  const [serverStatus, setServerStatus] = useState<ServerStatus | null>(null);
  const [isOffline, setIsOffline] = useState(false);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const res = await fetch(apiUrl('/api/health'));
        if (res.ok) {
          setServerStatus((await res.json()) as ServerStatus);
          setIsOffline(false);
        } else {
          setIsOffline(true);
        }
      } catch (_e) {
        setIsOffline(true);
      }
    };
    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex font-sans text-gray-900">
      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 h-8 bg-red-600 text-white text-xs font-bold flex items-center justify-center z-[100] animate-pulse">
          ⚠️ API SERVER IS OFFLINE - OFFLINE MODE
        </div>
      )}

      {/* Sidebar (Dark Theme) */}
      <aside
        className={cn(
          'w-64 bg-slate-900 text-slate-300 flex flex-col h-screen fixed inset-y-0 left-0 z-50 transition-all duration-300 shadow-xl',
          isOffline && 'top-8 h-[calc(100vh-8px)]',
        )}
      >
        {/* Header & University Switcher */}
        <div className="flex flex-col border-b border-slate-800 bg-slate-950">
          <div className="h-14 flex items-center px-4">
            <div className="flex items-center gap-2 text-white font-bold text-lg tracking-tight">
              <div className="p-1.5 bg-blue-600 rounded-lg">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              {t('common.appName')}

              {/* Context Debug Bridge/Badge */}
              {detectAppContext() === 'platform' && (
                <span className="ml-2 px-2 py-0.5 text-[10px] bg-red-600 text-white rounded-full uppercase tracking-wider font-bold">
                  Platform
                </span>
              )}
              {detectAppContext() === 'admin' && (
                <span className="ml-2 px-2 py-0.5 text-[10px] bg-emerald-600 text-white rounded-full uppercase tracking-wider font-bold">
                  Tenant
                </span>
              )}
            </div>
          </div>
          {/* University Switcher - Conditional Display */}
          {/* University Switcher Removed as per request (Step 1705) */}
          <div className="px-4 pb-4 hidden"></div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-1 custom-scrollbar">
          {menuConfig.map((group) => {
            const context = detectAppContext();

            // Hide System Management categories on Tenant Admin domain
            if (context === 'admin' && ['tenants', 'system_admin'].includes(group.categoryId)) {
              return null;
            }

            // Filter items based on role
            const visibleItems = group.items.filter((item) => {
              if (!item.roles) return true; // No roles defined = visible to all
              return item.roles.includes(user?.role || '');
            });

            // Hide tenant-required categories for system admin when no tenant selected
            if (group.requiresTenant && isSystemUser && !selectedTenant) {
              return null;
            }

            if (visibleItems.length === 0) return null;

            const Icon = group.icon;
            const isOpen = openCategories[group.categoryId];
            const isActiveGroup = visibleItems.some((item) => location.pathname.startsWith(item.path));

            return (
              <div key={group.categoryId} className="mb-2">
                {/* Category Header */}
                <button
                  type="button"
                  onClick={() => toggleCategory(group.categoryId)}
                  className={cn(
                    'w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 group',
                    isActiveGroup || isOpen
                      ? 'text-white bg-slate-800'
                      : 'text-slate-400 hover:text-white hover:bg-slate-800/50',
                  )}
                >
                  <div className="flex items-center gap-3">
                    <Icon
                      className={cn(
                        'w-5 h-5',
                        isActiveGroup ? 'text-blue-400' : 'text-slate-500 group-hover:text-slate-400',
                      )}
                    />
                    <span>{t(group.category)}</span>
                  </div>
                  {!group.notCollapsible &&
                    (isOpen ? (
                      <ChevronDown className="w-4 h-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-slate-600" />
                    ))}
                </button>

                {/* Category Items */}
                <div
                  className={cn(
                    'overflow-hidden transition-all duration-300 ease-in-out',
                    isOpen ? 'max-h-[500px] opacity-100 mt-1' : 'max-h-0 opacity-0',
                  )}
                >
                  <div className="ml-4 pl-3 border-l border-slate-700 space-y-1">
                    {group.items.map((item) => {
                      const isActive = location.pathname.startsWith(item.path);
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors truncate',
                            isActive
                              ? 'text-blue-400 bg-blue-900/20 font-medium'
                              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50',
                          )}
                        >
                          <span>{t(item.label)}</span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </nav>

        {/* Debug Panel (Demo) */}
        {!isOffline && serverStatus?.debug && (
          <div className="px-3 py-2 bg-black/30 border-t border-slate-800 text-[9px] font-mono text-slate-500 space-y-1">
            <div className="flex justify-between">
              <span>REGION:</span> <span className="text-blue-400">{serverStatus.debug.region}</span>
            </div>
            <div className="flex justify-between">
              <span>AUTH:</span> <span className="text-orange-400">{serverStatus.debug.auth}</span>
            </div>
            <div className="flex justify-between">
              <span>DB(PLT):</span>{' '}
              <span className={serverStatus.debug.databases?.platform ? 'text-green-400' : 'text-red-400'}>
                {serverStatus.debug.databases?.platform ? 'OK' : 'ERR'}
              </span>
            </div>
          </div>
        )}

        {/* Footer - Copyright only */}
        <div className="p-4 bg-slate-950 border-t border-slate-800 text-[10px] text-slate-500 text-center">
          &copy; {new Date().getFullYear()} {t('common.copyright')}
        </div>
      </aside>

      {/* Main Content Area */}
      <div className={cn('flex-1 ml-64 min-h-screen flex flex-col bg-gray-50', isOffline && 'mt-8')}>
        {/* Top Header (Shared) */}
        <header className="bg-white border-b border-gray-200 h-16 px-8 flex items-center justify-between shadow-sm sticky top-0 z-40">
          {/* Left: Breadcrumbs Component */}
          <AdminBreadcrumb />

          {/* Right: Language & User Profile */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <div className="h-6 w-px bg-gray-200 mx-2"></div>

            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="flex items-center gap-3 p-1.5 rounded-full hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
              >
                <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">
                  {user?.name?.charAt(0) || 'U'}
                </div>
                <div className="text-right hidden sm:block">
                  <div className="text-xs font-bold text-gray-700 leading-none">{user?.name}</div>
                  <div className="text-[10px] text-gray-500 leading-none mt-1">
                    {t(`auth.roles.${user?.role || 'viewer'}`)}
                  </div>
                </div>
                <ChevronsUpDown className="w-3 h-3 text-gray-400" />
              </button>

              {/* User Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute top-full right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden py-1 z-50 animate-in fade-in zoom-in-95 duration-100">
                  <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
                    <p className="text-xs font-medium text-gray-500">{t('sidebar.myAccount')}</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{user?.email}</p>
                  </div>
                  <Link
                    to="/settings/profile"
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                    onClick={() => setIsUserMenuOpen(false)}
                  >
                    <UserCog className="w-4 h-4" />
                    {t('profile.title')}
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <button
                    type="button"
                    onClick={() => {
                      logout();
                      navigate('/login');
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('auth.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Main View */}
        <main className="flex-1 p-8 overflow-auto">
          <Outlet context={{ selectedTenant }} />
        </main>

        {/* Dev Tools */}
        <UserSwitcher />
        <DebugInfoPanel />
      </div>
    </div>
  );
}
